import Vue from 'vue'

const API_VERSION = '2026-07'

const CREATE_CUSTOMER_ACCESS_TOKEN = `
  mutation CustomerAccessTokenCreate($input: CustomerAccessTokenCreateInput!) {
    customerAccessTokenCreate(input: $input) {
      customerAccessToken {
        accessToken
      }
      customerUserErrors {
        message
      }
    }
  }
`

const UPDATE_CUSTOMER = `
  mutation CustomerUpdate($customer: CustomerUpdateInput!, $customerAccessToken: String!) {
    customerUpdate(customer: $customer, customerAccessToken: $customerAccessToken) {
      customer {
        firstName
        lastName
        email
        phone
      }
      customerUserErrors {
        message
      }
    }
  }
`

const getErrors = payload => payload.customerUserErrors || payload.userErrors || []

const getErrorMessage = (payload, fallback) => {
  const errors = getErrors(payload)

  return errors.length ? errors.map(error => error.message).join(' ') : fallback
}

const request = (accessToken, query, variables) => window.fetch(`/api/${API_VERSION}/graphql.json`, {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'X-Shopify-Storefront-Access-Token': accessToken
  },
  body: JSON.stringify({ query, variables })
})
  .then(response => response.json().then(data => ({ response, data })))
  .then(({ response, data }) => {
    const graphqlError = data.errors && data.errors[0]

    if (!response.ok || graphqlError) {
      throw new Error((graphqlError && graphqlError.message) || 'Request failed.')
    }

    return data.data
  })

const setStatus = (statusElement, message, isError = false) => {
  statusElement.textContent = message
  statusElement.classList.toggle('redesign-account-details-form__status--error', isError)
}

const setFieldValue = (form, fieldName, value) => {
  const field = form.elements.namedItem(fieldName)

  if (field && typeof field.value !== 'undefined') {
    field.value = value || ''
    field.dispatchEvent(new Event('input', { bubbles: true }))
  }
}

const setDisplayValue = (root, fieldName, value) => {
  const valueElement = root.querySelector(`[data-account-details-value="${fieldName}"]`)

  if (valueElement) {
    valueElement.textContent = value || '—'
  }
}

export default root => Promise.all([
  window.enqueueModule('phone-input.vue')
]).then(() => {
  const vue = new Vue({ el: root })
  const initializedRoot = vue.$el

  const form = initializedRoot.querySelector('[data-account-details-form]')
  const editButton = initializedRoot.querySelector('[data-account-details-edit]')
  const cancelButton = initializedRoot.querySelector('[data-account-details-cancel]')
  const submitButton = initializedRoot.querySelector('[data-account-details-submit]')
  const statusElement = initializedRoot.querySelector('[data-account-details-status]')
  const storefrontAccessToken = root.dataset.storefrontAccessToken
  let currentEmail = root.dataset.customerEmail
  const missingTokenMessage = root.dataset.missingTokenMessage
  const updateErrorMessage = root.dataset.updateErrorMessage
  const successMessage = root.dataset.successMessage

  if (!form || !editButton || !cancelButton || !submitButton || !statusElement) {
    return
  }

  const resetPasswordField = () => {
    setFieldValue(form, 'password', '')

    vue.$nextTick(() => {
      const passwordInput = vue.$refs.accountDetailsPassword

      if (!passwordInput) return

      passwordInput.isValid = true
      passwordInput.dirty = false
      passwordInput.isActive = false
      passwordInput.revealed = false
      passwordInput.currentType = 'password'
    })
  }

  const setFormVisibility = isVisible => {
    form.hidden = !isVisible
    editButton.setAttribute('aria-expanded', String(isVisible))

    if (isVisible) {
      setFieldValue(form, 'first_name', root.dataset.customerFirstName)
      setFieldValue(form, 'last_name', root.dataset.customerLastName)
      setFieldValue(form, 'email', currentEmail)
      setFieldValue(form, 'phone', root.dataset.customerPhone)
      resetPasswordField()

      const passwordField = form.elements.namedItem('password')

      if (passwordField) {
        passwordField.setAttribute('autocomplete', 'current-password')
      }

      const firstNameField = form.elements.namedItem('first_name')

      if (firstNameField) {
        firstNameField.focus()
      }
    }
  }

  editButton.addEventListener('click', () => {
    setFormVisibility(form.hidden)
  })

  cancelButton.addEventListener('click', () => {
    setStatus(statusElement, '')
    setFormVisibility(false)
    editButton.focus()
  })

  form.addEventListener('submit', event => {
    event.preventDefault()

    if (!storefrontAccessToken) {
      setStatus(statusElement, missingTokenMessage, true)
      return
    }

    const formData = new FormData(form)
    const firstName = String(formData.get('first_name') || '').trim()
    const lastName = String(formData.get('last_name') || '').trim()
    const email = String(formData.get('email') || '').trim()
    const password = String(formData.get('password') || '')
    let phone = String(formData.get('phone') || '').trim()
    const emailField = form.elements.namedItem('email')
    const phonePicker = vue.$refs.accountDetailsPhone

    if (!firstName || !lastName || !email || !password || (emailField && !emailField.checkValidity())) {
      setStatus(statusElement, updateErrorMessage, true)
      return
    }

    if (phonePicker && typeof phonePicker.normalizeBeforeSubmit === 'function') {
      if (!phonePicker.normalizeBeforeSubmit()) {
        setStatus(statusElement, updateErrorMessage, true)
        return
      }

      phone = phonePicker.getE164().trim()
    }

    submitButton.disabled = true
    setStatus(statusElement, '')

    let customerAccessToken = ''

    const finishRequest = () => {
      customerAccessToken = ''
      submitButton.disabled = false
    }

    request(storefrontAccessToken, CREATE_CUSTOMER_ACCESS_TOKEN, {
      input: {
        email: currentEmail,
        password
      }
    })
      .then(tokenData => {
        const tokenPayload = tokenData.customerAccessTokenCreate

        if (!tokenPayload.customerAccessToken) {
          throw new Error(getErrorMessage(tokenPayload, updateErrorMessage))
        }

        customerAccessToken = tokenPayload.customerAccessToken.accessToken

        return request(storefrontAccessToken, UPDATE_CUSTOMER, {
          customer: {
            firstName,
            lastName,
            email,
            phone: phone || null
          },
          customerAccessToken
        })
      })
      .then(updateData => {
        const updatePayload = updateData.customerUpdate

        if (!updatePayload.customer) {
          throw new Error(getErrorMessage(updatePayload, updateErrorMessage))
        }

        const customer = updatePayload.customer

        root.dataset.customerEmail = customer.email || ''
        root.dataset.customerFirstName = customer.firstName || ''
        root.dataset.customerLastName = customer.lastName || ''
        root.dataset.customerPhone = customer.phone || ''
        currentEmail = customer.email || ''

        setDisplayValue(root, 'first_name', customer.firstName)
        setDisplayValue(root, 'last_name', customer.lastName)
        setDisplayValue(root, 'email', customer.email)
        setDisplayValue(root, 'phone', customer.phone)
        setStatus(statusElement, successMessage)
        resetPasswordField()
        finishRequest()
      }, error => {
        setStatus(statusElement, error.message || updateErrorMessage, true)
        resetPasswordField()
        finishRequest()
      })
  })
})
